import React, { useState, useEffect } from 'react';
import { Product } from '../db';
import { productsAPI, salesAPI } from '../backend';
import { Package, Tag } from 'lucide-react';
import ProductDialog from './ProductDialog';
import ProductImage from './ProductImage';
import BrandsView from './BrandsView';
import CartFooter from './CartFooter';
import CustomerView from './CustomerView';
import DeliveryView from './DeliveryView';
import PaymentView from './PaymentView';
import PaymentMethodView from './PaymentMethodView';
import SummaryView from './SummaryView';
import SuccessView from './SuccessView';
import { brandsOf, NO_BRAND } from '../utils/brands';

function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cart, setCart] = useState<any[]>([]);
  const [brandFilter, setBrandFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBrands, setShowBrands] = useState(false);
  const [currentView, setCurrentView] = useState<'products' | 'customer' | 'delivery' | 'payment' | 'paymentMethod' | 'summary' | 'success'>('products');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<string>('');
  const [deliveryCost, setDeliveryCost] = useState<number>(0);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('Por pagar');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [saleType, setSaleType] = useState<'Venta' | 'Muestra'>('Venta');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productsAPI.getAll();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product: any) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const getValidInitialQuantity = (product: any) => {
    if (!product) return 1;
    
    const cartItem = cart.find(item => item.id === product.id);
    const initialQuantity = cartItem?.quantity || 1;
    
    // Ensure initial quantity doesn't exceed stock
    return Math.min(initialQuantity, product.stock);
  };

  const handleAddToCart = async (quantity: number) => {
    const existingItem = cart.find(item => item.id === selectedProduct.id);
    
    if (existingItem) {
      // Update existing item quantity (replace, not add)
      setCart(cart.map(item =>
        item.id === selectedProduct.id
          ? { ...item, quantity }
          : item
      ));
    } else {
      // Add new item to cart
      setCart([...cart, { ...selectedProduct, quantity }]);
    }
    
    console.log(`${existingItem ? 'Actualizados' : 'Agregados'} ${quantity} de ${selectedProduct.name} al carrito`);
  };

  const handleRemoveFromCart = async () => {
    setCart(cart.filter(item => item.id !== selectedProduct.id));
    setIsDialogOpen(false);
  };

  const handleRemoveFromCartById = async (id: number) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const handleNext = () => {
    setCurrentView('customer');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando productos...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const handleCustomerSelected = (customer: any) => {
    setSelectedCustomer(customer);
    setCurrentView('delivery');
  };

  const handleBackToProducts = () => {
    setCurrentView('products');
  };

  const handleBackToCustomer = () => {
    setCurrentView('customer');
  };

  const handleDeliverySelected = (option: string, cost: number) => {
    setSelectedDeliveryOption(option);
    setDeliveryCost(cost);
    setCurrentView('payment');
  };

  const handleBackToDelivery = () => {
    setCurrentView('delivery');
  };

  const handlePaymentSelected = (status: string) => {
    setSaleType('Venta');
    setSelectedPaymentStatus(status);
    setCurrentView('paymentMethod');
  };

  const handleSampleSelected = () => {
    setSaleType('Muestra');
    setSelectedPaymentStatus('Pagado');
    setSelectedPaymentMethod('');
    setCurrentView('summary');
  };

  const handleBackToPayment = () => {
    setCurrentView('payment');
  };

  const handlePaymentMethodSelected = (method: string) => {
    setSelectedPaymentMethod(method);
    setCurrentView('summary');
  };

  const handleBackToPaymentMethod = () => {
    setCurrentView('paymentMethod');
  };

  const handleConfirmOrder = async () => {
    setSaving(true);
    // Calculate totals
    const isSample = saleType === 'Muestra';
    const subtotal = isSample ? 0 : cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const total = subtotal + deliveryCost;

    // Use the selected payment status
    const paymentStatus = isSample && total === 0 ? 'Pagado' : selectedPaymentStatus;

    // Create the sale object
    const newSale = {
      id: Date.now(), // Generate a unique ID
      clientId: selectedCustomer.id,
      products: cart.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: isSample ? 0 : item.price,
        cost: item.cost
      })),
      deliveryMethod: selectedDeliveryOption,
      paymentStatus: paymentStatus,
      paymentMethod: selectedPaymentMethod,
      total: total,
      date: new Date().toISOString() // Store full ISO string with date and time
    };

    // Save the sale to the database
    try {
      await salesAPI.create({
        client_id: newSale.clientId,
        delivery_method: newSale.deliveryMethod,
        payment_status: newSale.paymentStatus,
        payment_method: isSample ? undefined : newSale.paymentMethod,
        sale_type: saleType,
        total: newSale.total,
        date: newSale.date,
        products: newSale.products.map(p => ({
          product_id: p.id,
          quantity: p.quantity,
          price: p.price,
          cost: p.cost, // Include the cost in the sale_products table
          quantity_paid: isSample || paymentStatus === 'Pagado' ? p.quantity : 0,
        })),
      });
      
      // Update product stock in database only after sale is confirmed
      for (const cartItem of cart) {
        const product = products.find(p => p.id === cartItem.id);
        if (product) {
          const updatedStock = product.stock - cartItem.quantity;
          await productsAPI.update(cartItem.id, { stock: updatedStock });
        }
      }
      
      // Reload products to reflect updated stock
      await loadProducts();
      
      console.log('¡Pedido confirmado y guardado!');
      console.log('Venta creada:', newSale);
      setCurrentView('success');
    } catch (err) {
      console.error('Error creating sale:', err);
      // Handle error appropriately - maybe show an error message to user
    } finally {
      setSaving(false);
    }
  };

  const handleNewSale = () => {
    setCart([]);
    setSelectedCustomer(null);
    setSelectedDeliveryOption('');
    setDeliveryCost(0);
    setSelectedPaymentStatus('Por pagar');
    setSelectedPaymentMethod('');
    setSaleType('Venta');
    setBrandFilter('');
    setSearchTerm('');
    setCurrentView('products');
    // Reload products to ensure we have fresh stock data
    loadProducts();
  };

  if (saving) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Guardando venta...</p>
      </div>
    );
  }

  if (currentView === 'customer') {
    return (
      <CustomerView
        cart={cart}
        onBack={handleBackToProducts}
        onCustomerSelected={handleCustomerSelected}
      />
    );
  }

  if (currentView === 'delivery') {
    return (
      <DeliveryView
        cart={cart}
        customer={selectedCustomer}
        onBack={handleBackToCustomer}
        onDeliverySelected={handleDeliverySelected}
      />
    );
  }

  if (currentView === 'payment') {
    return (
      <PaymentView
        cart={cart}
        customer={selectedCustomer}
        deliveryOption={selectedDeliveryOption}
        onBack={handleBackToDelivery}
        onPaymentSelected={handlePaymentSelected}
        onSampleSelected={handleSampleSelected}
      />
    );
  }

  if (currentView === 'paymentMethod') {
    return (
      <PaymentMethodView
        cart={cart}
        customer={selectedCustomer}
        deliveryOption={selectedDeliveryOption}
        onBack={handleBackToPayment}
        onPaymentMethodSelected={handlePaymentMethodSelected}
      />
    );
  }

  if (currentView === 'summary') {
    return (
      <SummaryView
        cart={cart}
        customer={selectedCustomer}
        deliveryOption={selectedDeliveryOption}
        deliveryCost={deliveryCost}
        paymentStatus={selectedPaymentStatus}
        paymentMethod={selectedPaymentMethod}
        saleType={saleType}
        onBack={saleType === 'Muestra' ? handleBackToPayment : handleBackToPaymentMethod}
        onConfirmOrder={handleConfirmOrder}
        onRemoveItem={handleRemoveFromCartById}
      />
    );
  }

  if (currentView === 'success') {
    return (
      <SuccessView onNewSale={handleNewSale} deliveryMethod={selectedDeliveryOption} />
    );
  }

  // Rendered as a sub-view rather than a separate route so the cart survives
  // the round trip — the component (and its state) stays mounted.
  if (showBrands) {
    return (
      <BrandsView
        products={products}
        onBack={() => setShowBrands(false)}
        onSelectBrand={brand => { setBrandFilter(brand); setShowBrands(false); }}
      />
    );
  }

  const brands = brandsOf(products);
  const term = searchTerm.trim().toLowerCase();
  const visibleProducts = products.filter(product => {
    const matchesBrand = !brandFilter
      || (brandFilter === NO_BRAND ? !product.brand : product.brand === brandFilter);
    const matchesSearch = !term
      || product.name.toLowerCase().includes(term)
      || (product.brand || '').toLowerCase().includes(term);
    return matchesBrand && matchesSearch;
  });

  return (
    <div className="main-view">
      <div className="search-section">
        <input
          type="text"
          placeholder="Buscar productos por nombre o marca..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {brands.length > 0 && (
          <select
            value={brandFilter}
            onChange={e => setBrandFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">Todas las marcas</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
            <option value={NO_BRAND}>{NO_BRAND}</option>
          </select>
        )}
        <button className="btn-secondary" onClick={() => setShowBrands(true)}>
          <Tag size={16} />
          <span>Marcas</span>
        </button>
      </div>
      <div className="products-grid">
        {visibleProducts.map(product => {
          const cartItem = cart.find(item => item.id === product.id);
          const isSelected = !!cartItem;
          // Calculate available stock (actual stock minus what's already in cart)
          const reservedQuantity = cartItem?.quantity || 0;
          const availableStock = product.stock - reservedQuantity;

          return (
            <div
              className={`product-card${isSelected ? ' selected' : ''}`}
              key={product.id}
              onClick={() => handleProductClick(product)}
            >
              <div className="product-image-wrapper">
                <ProductImage src={product.image} alt={product.name} className="product-image" />
                {isSelected && (
                  <div className="product-selected-badge">
                    {cartItem.quantity}
                  </div>
                )}
              </div>
              <div className="product-info">
                {product.brand && <div className="product-brand">{product.brand}</div>}
                <div className="product-name">{product.name}</div>
                <div className="product-price">${product.price.toFixed(2)}</div>
                <div className="product-stock">Stock: {availableStock}</div>
              </div>
            </div>
          );
        })}
      </div>
      {visibleProducts.length === 0 && (
        <div className="no-products">
          <div className="no-products-icon">
            <Package size={48} />
          </div>
          <h3>No hay productos</h3>
          <p>
            {products.length === 0
              ? 'Aún no hay productos en el catálogo.'
              : 'No se encontraron productos con esos filtros.'}
          </p>
        </div>
      )}
      <ProductDialog
        product={selectedProduct}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onAdd={handleAddToCart}
        onRemove={handleRemoveFromCart}
        initialQuantity={getValidInitialQuantity(selectedProduct)}
        cart={cart}
      />
      <CartFooter cart={cart} onNext={handleNext} />
    </div>
  );
}

export default Products; 